/**
 * PaperViewer.tsx — Main React component for the PDF viewer panel
 *
 * Replaces paper-viewer.js + annotations.js with:
 *   - PdfLoader / PdfHighlighter from react-pdf-highlighter-extended
 *   - Custom HighlightContainer for typed annotations
 *   - SelectionTip for annotation creation
 *   - Event bridge to the vanilla JS sidebar (Notes panel, etc.)
 *
 * Mounts into <div id="pdf-react-root"> inside the existing Jinja template.
 * The sidebar, panel resizer, chat, and all other UI remain vanilla JS.
 */
import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  PdfLoader,
  PdfHighlighter,
} from "react-pdf-highlighter-extended";
import type { PdfHighlighterUtils, Highlight } from "react-pdf-highlighter-extended";
import { version as pdfjsVersion } from "pdfjs-dist/package.json";
import "react-pdf-highlighter-extended/dist/esm/style/PdfHighlighter.css";
import "react-pdf-highlighter-extended/dist/esm/style/TextHighlight.css";
import "react-pdf-highlighter-extended/dist/esm/style/AreaHighlight.css";
import "react-pdf-highlighter-extended/dist/esm/style/MouseSelection.css";
import "react-pdf-highlighter-extended/dist/esm/style/pdf_viewer.css";

/**
 * Worker URL is derived from the installed pdfjs-dist version at build time.
 * This ensures the API and Worker versions always match, permanently.
 */
const PDFJS_WORKER_SRC = `https://unpkg.com/pdfjs-dist@${pdfjsVersion}/build/pdf.worker.min.mjs`;

import type { RemyxHighlight, AnnotationType } from "./types";
import { RemyxHighlightContainer } from "./HighlightContainer";
import { SelectionTip } from "./SelectionTip";
import type { SelectionData } from "./SelectionTip";
import {
  fetchAnnotations,
  createAnnotation,
  deleteAnnotation,
  rawToHighlight,
} from "./api";

/* ── Global window refs ─────────────────────────────────────── */
declare const window: Window & {
  __PAPER__?: { id?: string; extra?: any; pdf_url?: string; url?: string };
  __IS_AUTHENTICATED__?: boolean;
  __EXPERIMENT_CONTEXT_ID__?: string | null;
};

/* ── Component ──────────────────────────────────────────────── */

export const PaperViewer: React.FC = () => {
  const paper = window.__PAPER__ || {};
  const paperId = paper.id || "";
  const isAuth = !!window.__IS_AUTHENTICATED__;

  // Resolve PDF URL (same logic as old paper-viewer.js)
  let pdfUrl =
    paper.extra?.pdf_url || (paper as any).pdf_url || paper.url || "";
  if (pdfUrl.startsWith("http://arxiv.org/"))
    pdfUrl = pdfUrl.replace("http://", "https://");

  const [highlights, setHighlights] = useState<RemyxHighlight[]>([]);
  const [annotationMode, setAnnotationMode] = useState<string>("personal");
  const [isDarkMode, setIsDarkMode] = useState<boolean>(
    localStorage.getItem("remyx_pdf_dark_mode") !== "0"
  );

  const highlighterRef = useRef<PdfHighlighterUtils>();

  /* ── Load annotations from API ────────────────────────────── */
  useEffect(() => {
    if (!paperId || !isAuth) return;

    fetchAnnotations(paperId)
      .then(({ highlights: hl, mode }) => {
        setHighlights(hl);
        setAnnotationMode(mode);
        // Notify sidebar
        broadcastAnnotations(hl, mode);
      })
      .catch((err) => console.error("[PaperViewer] Load annotations:", err));
  }, [paperId, isAuth]);

  /* ── Broadcast to vanilla JS sidebar ──────────────────────── */
  const broadcastAnnotations = useCallback(
    (hl: RemyxHighlight[], mode?: string) => {
      document.dispatchEvent(
        new CustomEvent("annotations:loaded", {
          detail: {
            annotations: hl.map(highlightToSidebarFormat),
            mode: mode || annotationMode,
          },
        })
      );
    },
    [annotationMode]
  );

  /** Convert library format back to sidebar-friendly shape */
  function highlightToSidebarFormat(h: RemyxHighlight) {
    return {
      id: h.id,
      annotation_type: h.annotation_type,
      selected_text: h.content?.text || "",
      note_text: h.note_text,
      page_number: h.position.pageNumber,
      position_data: h.position,
      author: h.author,
      experiment_id: h.experiment_id,
    };
  }

  /* ── Create annotation ────────────────────────────────────── */
  const handleCreate = useCallback(
    async (type: AnnotationType, selectionData: SelectionData, noteText?: string) => {
      if (!paperId) return;

      try {
        const saved = await createAnnotation(paperId, {
          annotation_type: type,
          selected_text: selectionData.selectedText,
          note_text: noteText || null,
          page_number: selectionData.pageNumber,
          position_data: selectionData.position,
        });

        if (saved) {
          const newHl = rawToHighlight(saved);
          if (!saved.position_data || Object.keys(saved.position_data).length === 0) {
            newHl.position = selectionData.position;
          }

          const updated = [...highlights, newHl];
          setHighlights(updated);
          broadcastAnnotations(updated);
        }
      } catch (err) {
        console.error("[PaperViewer] Create annotation:", err);
        alert("Failed to create annotation: " + (err as Error).message);
      }
    },
    [paperId, highlights, broadcastAnnotations]
  );

  /* ── Delete annotation ────────────────────────────────────── */
  const handleDelete = useCallback(
    async (id: string) => {
      if (!paperId) return;
      try {
        await deleteAnnotation(paperId, id);
        const updated = highlights.filter((h) => h.id !== id);
        setHighlights(updated);
        broadcastAnnotations(updated);
      } catch (err) {
        console.error("[PaperViewer] Delete annotation:", err);
        alert("Delete failed: " + (err as Error).message);
      }
    },
    [paperId, highlights, broadcastAnnotations]
  );

  /* ── Listen for sidebar events ────────────────────────────── */
  useEffect(() => {
    // Sidebar card click → scroll to highlight
    const onScrollTo = (e: Event) => {
      const id = (e as CustomEvent).detail?.id;
      if (!id || !highlighterRef.current) return;
      const hl = highlights.find((h) => h.id === id);
      if (hl) {
        highlighterRef.current.scrollToHighlight(hl as Highlight);
      }
    };

    // Sidebar delete → remove highlight
    const onDeleteFromSidebar = (e: Event) => {
      const id = (e as CustomEvent).detail?.id;
      if (id) handleDelete(id);
    };

    // Sidebar edit → update highlight in React state
    const onUpdatedFromSidebar = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      const { id, annotation } = detail;
      if (!id || !annotation) return;
      setHighlights((prev) => {
        const updated = prev.map((h) => {
          if (h.id !== id) return h;
          return {
            ...h,
            annotation_type: annotation.annotation_type || h.annotation_type,
            note_text: annotation.note_text ?? h.note_text,
          };
        });
        return updated;
      });
    };

    document.addEventListener("annotations:scrollTo", onScrollTo);
    document.addEventListener("annotations:delete", onDeleteFromSidebar);
    document.addEventListener("annotations:updated", onUpdatedFromSidebar);
    return () => {
      document.removeEventListener("annotations:scrollTo", onScrollTo);
      document.removeEventListener("annotations:delete", onDeleteFromSidebar);
      document.removeEventListener("annotations:updated", onUpdatedFromSidebar);
    };
  }, [highlights, handleDelete]);

  /* ── Dark mode ────────────────────────────────────────────── */
  const toggleDarkMode = useCallback(() => {
    setIsDarkMode((prev) => {
      const next = !prev;
      localStorage.setItem("remyx_pdf_dark_mode", next ? "1" : "0");
      return next;
    });
  }, []);

  // Expose toggle for the floating button (which is outside React)
  useEffect(() => {
    (window as any).__paperViewer = {
      toggleDarkMode,
      isDarkMode: () => isDarkMode,
      scrollToHighlight: (id: string) => {
        const hl = highlights.find((h) => h.id === id);
        if (hl) highlighterRef.current?.scrollToHighlight(hl as Highlight);
      },
    };
  }, [toggleDarkMode, isDarkMode, highlights]);

  /* ── Reading progress bar ────────────────────────────────── */
  useEffect(() => {
    const progressBar = document.getElementById("reading-progress");
    if (!progressBar) return;

    // The library renders a scrollable div inside .PdfHighlighter
    const findScroller = () => {
      const root = document.getElementById("pdf-react-root");
      // PdfHighlighter uses a div with overflow:auto as its scroll container
      const el =
        root?.querySelector(".PdfHighlighter") as HTMLElement | null;
      return el;
    };

    let scroller: HTMLElement | null = null;

    const onScroll = () => {
      if (!scroller) return;
      const { scrollTop, scrollHeight, clientHeight } = scroller;
      const max = scrollHeight - clientHeight;
      const pct = max > 0 ? Math.min(100, (scrollTop / max) * 100) : 0;
      progressBar.style.width = `${pct}%`;
    };

    // Retry a few times since PdfLoader renders async
    let attempts = 0;
    const tryAttach = () => {
      scroller = findScroller();
      if (scroller) {
        scroller.addEventListener("scroll", onScroll, { passive: true });
        onScroll(); // initial
      } else if (attempts < 20) {
        attempts++;
        setTimeout(tryAttach, 500);
      }
    };
    tryAttach();

    return () => {
      if (scroller) scroller.removeEventListener("scroll", onScroll);
    };
  }, [pdfUrl]);

  /* ── Render ───────────────────────────────────────────────── */

  if (!pdfUrl) {
    return (
      <p style={{ color: "#888", fontSize: "14px", padding: "32px" }}>
        No PDF URL available.
      </p>
    );
  }

  return (
    <div
      style={{ width: "100%", height: "100%", position: "relative" }}
      className={isDarkMode ? "pdf-dark-mode" : ""}
    >
      {/* Floating controls — outside the PdfHighlighter so they're always on top */}
      <div
        style={{
          position: "absolute",
          top: "8px",
          right: "12px",
          zIndex: 15,
          display: "flex",
          gap: "4px",
          alignItems: "center",
        }}
      >
        <FloatingButton
          title={isDarkMode ? "Light mode" : "Dark mode"}
          onClick={toggleDarkMode}
        >
          {isDarkMode ? (
            <SunIcon />
          ) : (
            <MoonIcon />
          )}
        </FloatingButton>
      </div>

      <PdfLoader
        document={pdfUrl}
        workerSrc={PDFJS_WORKER_SRC}
        beforeLoad={() => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "256px",
              color: "#888",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <p style={{ fontSize: "14px" }}>Loading PDF…</p>
            </div>
          </div>
        )}
      >
        {(pdfDocument) => (
          <PdfHighlighter
            pdfDocument={pdfDocument}
            highlights={isAuth ? highlights : []}
            utilsRef={(utils) => {
              highlighterRef.current = utils;
            }}
            selectionTip={
              isAuth ? <SelectionTip onConfirm={handleCreate} /> : undefined
            }
            pdfScaleValue="auto"
            style={{
              height: "100%",
            }}
          >
            <RemyxHighlightContainer onDelete={handleDelete} />
          </PdfHighlighter>
        )}
      </PdfLoader>
    </div>
  );
};

/* ── Utility components ─────────────────────────────────────── */

const FloatingButton: React.FC<{
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ title, onClick, children }) => (
  <button
    title={title}
    onClick={onClick}
    style={{
      width: "32px",
      height: "32px",
      borderRadius: "50%",
      background: "rgba(23,23,23,0.75)",
      border: "1px solid rgba(255,255,255,0.08)",
      backdropFilter: "blur(8px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      color: "#888",
      transition: "all 0.15s",
    }}
    onMouseOver={(e) => {
      (e.currentTarget as HTMLElement).style.background =
        "rgba(40,40,40,0.9)";
      (e.currentTarget as HTMLElement).style.color = "#fff";
    }}
    onMouseOut={(e) => {
      (e.currentTarget as HTMLElement).style.background =
        "rgba(23,23,23,0.75)";
      (e.currentTarget as HTMLElement).style.color = "#888";
    }}
  >
    {children}
  </button>
);

const SunIcon = () => (
  <svg
    width="16"
    height="16"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth="1.5"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z"
    />
  </svg>
);

const MoonIcon = () => (
  <svg
    width="16"
    height="16"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth="1.5"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z"
    />
  </svg>
);
